// Code modified from: https://github.com/jonataswalker/ol-geocoder version: July 30, 2019
// The MIT License (MIT)
//
// Copyright (c) 2015 Jonatas Walker
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { OpenStreetMap } from './providers/osm';
import axios from '@/axios';

// Geocoder Provider types
export const PROVIDERS = {
  osm: OpenStreetMap
};

/**
 * @class GeocoderController
 */
export class GeocoderController {
  /**
   * @constructor
   * @param {String} providerName name of Provider.
   * @param {Object} options config options for Provider
   * @param {Object} parent callback parent
   */
  constructor (providerName, options, parent) {
    this.options = options;

    // Must have Provider class defined for name
    if (!Object.prototype.hasOwnProperty.call(PROVIDERS, providerName)) {
      console.warn(`No class defined for Geocoder Provider: '${providerName}'`);
      return;
    }
    // Create Provider from name via Factory Method
    this.provider = new PROVIDERS[providerName]();
    this.parent = parent;
  }

  async query (q, type) {
    const parameters = this.provider.getParameters({
      query: q,
      querytype: type,
      key: this.options.key,
      lang: this.options.lang,
      countrycodes: this.options.countrycodes,
      limit: this.options.limit
    });

    // get jwt token
    const jwtToken = localStorage.getItem('jwt');
    if (!jwtToken) {
      throw new Error('JWT token not found in localStorage');
    }
    // Build axios config from provider parameters
    const method = (parameters.method || 'get').toLowerCase();
    const axiosConfig = {
      method,
      url: parameters.url,
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json'
      }
    };

    if (method === 'get') {
      if (parameters.params) axiosConfig.params = parameters.params;
    } else {
      // POST/PUT/etc: prefer explicit body, fall back to params if body missing
      axiosConfig.data = parameters.body || parameters.params || { query: q };
    }

    const response = await axios(axiosConfig);

    // Pass the raw response payload to provider for normalization
    return this.provider.handleResponse(response.data);
  }

  /**
   * Fetch authenticated user's regions from server.
   * Centralizes JWT header handling so callers don't need to duplicate logic.
   * Returns the raw results array (or throws on error).
   */
  async fetchAuthRegions () {
    const optionUrl = '/users/me/auth-region';
    const jwtToken = localStorage.getItem('jwt');
    if (!jwtToken) {
      throw new Error('JWT token not found in localStorage');
    }
    const response = await axios.post(optionUrl, {}, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json'
      }
    });
    // Expecting response.data.results to be the array of regions
    return response.data.results;
  }
}
